import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { LoginPayload, PermissionKey } from '@lkq/shared';
import * as authService from '@/services/auth.service';
import { onSessionExpired, onMembershipBlocked, errorCode } from '@/services/api';
import type { MePayload } from '@/types/entities';

interface AuthContextValue {
  me: MePayload | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<MePayload>;
  logout: () => Promise<void>;
  hasPermission: (key: PermissionKey) => boolean;
  /** Role-based landing path after login (docs/03). */
  homePath: (me: MePayload) => string;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: authService.fetchMe,
    retry: (count, error) => errorCode(error) !== 'unauthorized' && count < 1,
    staleTime: 60_000,
  });

  useEffect(() => {
    onSessionExpired(() => {
      queryClient.setQueryData(['me'], null);
      navigate('/login', { replace: true });
    });
    // Mid-session membership block (403 garage_*) → branded block screen (docs/06).
    onMembershipBlocked((status) => {
      navigate(`/membership-unavailable?status=${status}`, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const me = data ?? null;

  const homePath = useCallback((payload: MePayload): string => {
    if (payload.user.garageId) {
      return payload.user.membershipStatus === 'pending' ? '/onboarding' : '/';
    }
    if (payload.user.isPlatformAdmin) return '/admin';
    if (payload.user.adminConceptIds.length > 0) return '/concept';
    return '/login';
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const result = await authService.login(payload);
      // Drop the previous session's cached data so user-scoped queries (concepts,
      // the concept-portal skin, etc.) refetch for the new user — otherwise a
      // login switch keeps the old theme until a manual refresh.
      queryClient.clear();
      queryClient.setQueryData(['me'], result);
      return result;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await authService.logout().catch(() => undefined);
    queryClient.setQueryData(['me'], null);
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [queryClient, navigate]);

  const hasPermission = useCallback(
    (key: PermissionKey) => me?.user.permissions.includes(key) ?? false,
    [me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      isLoading,
      isAuthenticated: !!me,
      login,
      logout,
      hasPermission,
      homePath,
      refresh: async () => {
        await refetch();
      },
    }),
    [me, isLoading, login, logout, hasPermission, homePath, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
